import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Layout, Menu, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { useModalStore } from '@/store/useModalStore';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import newLogo from '@/assets/home/new-logo.png';

const { Header } = Layout;

const SCROLL_THRESHOLD = 16;

export interface AixueHeaderProps {
  title?: string;
}

export default function AixueHeader({ title = 'Aixue' }: AixueHeaderProps) {
  const { openModalMask } = useModalStore();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname || '';
  const [isScrolled, setIsScrolled] = useState(false);
  const [noTransition, setNoTransition] = useState(false);
  const [productsTriggerPressed, setProductsTriggerPressed] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onMouseUp = () => setProductsTriggerPressed(false);
    document.addEventListener('mouseup', onMouseUp);
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, []);

  useLayoutEffect(() => {
    const atTop = window.scrollY <= SCROLL_THRESHOLD;
    setIsScrolled(!atTop);
    setNoTransition(true);
    const id = setTimeout(() => setNoTransition(false), 400);
    return () => clearTimeout(id);
  }, [pathname]);

  const selectedKey = (() => {
    if (pathname.startsWith('/aixue-phonics') || pathname.startsWith('/aixue-ai'))
      return 'products';
    if (pathname.startsWith('/aixue-about')) return 'about';
    if (pathname.startsWith('/aixue-join')) return 'careers';
    if (pathname.startsWith('/aixue-download')) return 'downloads';
    return 'home';
  })();

  const productsDropdownSelectedKey = pathname.startsWith('/aixue-ai')
    ? 'products-ai'
    : pathname.startsWith('/aixue-phonics')
      ? 'products-phonics'
      : undefined;
  const productsItems: MenuProps['items'] = [
    {
      key: 'products-ai',
      label: <span className="aixue-menu-link">爱学AI一对一导师</span>,
      onClick: () => navigate('/aixue-ai'),
      className:
        productsDropdownSelectedKey === 'products-ai' ? styles.dropdownItemSelected : undefined,
    },
    { type: 'divider', key: 'products-divider' },
    {
      key: 'products-phonics',
      label: <span className="aixue-menu-link">爱学AI自然拼读</span>,
      onClick: () => navigate('/aixue-phonics'),
      className:
        productsDropdownSelectedKey === 'products-phonics'
          ? styles.dropdownItemSelected
          : undefined,
    },
  ];
  const menuItems: MenuProps['items'] = [
    {
      key: 'home',
      label: (
        <Link className="aixue-menu-link" to="/">
          首页
        </Link>
      ),
    },
    {
      key: 'products',
      label: (
        <span
          className={styles.productsDropdownWrap}
          data-trigger-active={productsTriggerPressed ? 'true' : undefined}
          onMouseDown={() => setProductsTriggerPressed(true)}
          onMouseUp={() => setProductsTriggerPressed(false)}
          onMouseLeave={() => setProductsTriggerPressed(false)}
        >
          <Dropdown
            trigger={['hover']}
            menu={{ items: productsItems }}
            overlayClassName="aixue-menu-overlay"
            placement="bottom"
            mouseLeaveDelay={0.4}
            arrow={true}
            transitionName=""
          >
            <span className={`aixue-menu-link ${styles.productsDropdownTrigger}`}>产品中心</span>
          </Dropdown>
        </span>
      ),
    },
    {
      key: 'about',
      label: (
        <Link className="aixue-menu-link" to="/aixue-about">
          关于我们
        </Link>
      ),
    },
    {
      key: 'careers',
      label: (
        <Link className="aixue-menu-link" to="/aixue-join">
          加入我们
        </Link>
      ),
    },
    {
      key: 'downloads',
      label: (
        <Link className="aixue-menu-link" to="/aixue-download">
          下载中心
        </Link>
      ),
    },
  ];

  return (
    <>
      <Header
        className={`${styles.header} ${isScrolled ? styles.scrolled : ''} ${noTransition ? styles.noTransition : ''}`}
      >
        <div
          className={`${styles.inner} ${isScrolled ? styles.innerScrolled : ''}`}
          style={{ '--logo-url': `url(${newLogo})` } as React.CSSProperties}
        >
          <div className={styles['inner-base']}>
            <div className={styles.logo}></div>
            <Menu
              className={styles.menu}
              mode="horizontal"
              theme="light"
              triggerSubMenuAction="hover"
              selectedKeys={selectedKey ? [selectedKey] : []}
              items={menuItems}
            />
          </div>
        </div>
      </Header>
      <div className={styles.headerSpacer} aria-hidden />
    </>
  );
}
